import { Card } from "@shared/components/ui/card";
import { format } from "date-fns";
import {
  AlertCircle,
  CalendarIcon,
  CheckCircle2,
  Clock,
  Eye,
  MapPin,
  Package,
  Phone,
  Store,
  Truck,
  User,
  UserCheck,
  UserPlus,
} from "lucide-react";
import { Separator } from "@shared/components/ui/separator";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LivreurAssignmentDialog from "./LivreurAssignmentDialog";

const statusColors = {
  order_received: "bg-gray-100 text-gray-800 border-gray-200",
  assigned: "bg-indigo-100 text-indigo-800 border-indigo-200",
  picked_up: "bg-purple-100 text-purple-800 border-purple-200",
  in_transit: "bg-blue-100 text-blue-800 border-blue-200",
  out_for_delivery: "bg-cyan-100 text-cyan-800 border-cyan-200",
  delivered: "bg-green-100 text-green-800 border-green-200",
  returned: "bg-orange-100 text-orange-800 border-orange-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};

const statusIcons = {
  delivered: <CheckCircle2 className="h-4 w-4 text-green-500" />,
  out_for_delivery: <Truck className="h-4 w-4 text-cyan-500" />,
  in_transit: <Truck className="h-4 w-4 text-blue-500" />,
  picked_up: <Package className="h-4 w-4 text-purple-500" />,
  assigned: <UserCheck className="h-4 w-4 text-indigo-500" />,
  order_received: <Clock className="h-4 w-4 text-gray-500" />,
  returned: <AlertCircle className="h-4 w-4 text-orange-500" />,
  cancelled: <AlertCircle className="h-4 w-4 text-red-500" />,
};

const PackageCardForLivreurManagement = ({ packageData, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();

  return (
    <Card className="w-full shadow-md transition-transform duration-300 hover:bg-background/40 hover:scale-[0.99]">
      <div className="flex justify-between items-start flex-col p-4 space-y-3">
        {/* Header Section */}
        <div className="w-full flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <h3 className="text-sm font-semibold truncate">
              {packageData.customer.name}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={
                statusColors[packageData.status] || statusColors.order_received
              }
            >
              <div className="mr-1">{statusIcons[packageData.status]}</div>
              {t(packageData.status?.replace(/_/g, " ") || "Unknown")}
            </Badge>
          </div>
        </div>

        <Separator />

        {/* Customer Details */}
        <div className="w-full space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{packageData.customer.phone}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span>{packageData.customer.city}</span>
            </div>
          </div>

          {/* Client Info (Admin/Manager view) */}
          {(auth.isAdmin || auth.isManager) && packageData.client && (
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
              <Store className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">
                {packageData.client.brandName || packageData.client.fullName}
              </span>
            </div>
          )}

          {/* Tracking Number */}
          {packageData.trackingNumber && (
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-muted-foreground" />
              <Badge variant="outline" className="text-xs">
                {packageData.trackingNumber}
              </Badge>
            </div>
          )}

          {/* Address */}
          {packageData.customer.address && (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">{t("Address")}:</span>{" "}
              {packageData.customer.address}
            </div>
          )}
        </div>

        <Separator />

        {/* Livreur Assignment Status */}
        <div className="w-full">
          {packageData.livreur ? (
            <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-950/20 rounded-md border border-green-200 dark:border-green-800">
              <UserCheck className="h-4 w-4 text-green-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-green-900 dark:text-green-100">
                  {packageData.livreur.fullName || packageData.livreur.email}
                </p>
                {packageData.livreur.phone && (
                  <p className="text-xs text-green-700 dark:text-green-300">
                    {packageData.livreur.phone}
                  </p>
                )}
              </div>
              {packageData.livreurDeliveryPrice > 0 && (
                <Badge
                  variant="outline"
                  className="bg-green-50 border-green-300"
                >
                  {packageData.livreurDeliveryPrice} DH
                </Badge>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 p-2 bg-orange-50 dark:bg-orange-950/20 rounded-md border border-orange-200 dark:border-orange-800">
              <User className="h-4 w-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-900 dark:text-orange-100">
                {t("Not assigned to any livreur")}
              </span>
            </div>
          )}
        </div>

        <Separator />

        {/* Timestamps */}
        <div className="w-full flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center text-xs text-muted-foreground bg-muted/50 rounded-md p-1.5">
            <CalendarIcon className="w-3.5 h-3.5 mr-1" />
            <span>
              {t("Created")}:{" "}
              {format(new Date(packageData.createdAt), "dd/MM/yyyy HH:mm")}
            </span>
          </div>
          {packageData.assignedAt && (
            <div className="flex items-center text-xs text-muted-foreground bg-green-50 dark:bg-green-950/20 rounded-md p-1.5">
              <UserCheck className="w-3.5 h-3.5 mr-1" />
              <span>
                {t("Assigned")}:{" "}
                {format(new Date(packageData.assignedAt), "dd/MM/yyyy HH:mm")}
              </span>
            </div>
          )}
        </div>

        <Separator />

        {/* Action Buttons */}
        <div className="w-full flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/packages/${packageData._id}/view`)}
          >
            <Eye className="h-4 w-4 mr-1" />
            {t("View")}
          </Button>

          {(auth.isAdmin || auth.isManager) && (
            <LivreurAssignmentDialog
              packageData={packageData}
              onSuccess={onSuccess}
              trigger={
                <Button
                  size="sm"
                  variant={packageData.livreur ? "outline" : "default"}
                >
                  {packageData.livreur ? (
                    <>
                      <UserCheck className="h-4 w-4 mr-1" />
                      {t("Reassign")}
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-1" />
                      {t("Assign")}
                    </>
                  )}
                </Button>
              }
            />
          )}
        </div>
      </div>
    </Card>
  );
};

export default PackageCardForLivreurManagement;
